import $ from 'jquery';
import { parseTemplate, warn } from '../common/helpers';
import OrderProduct from './OrderProduct';
import Reason from './Reason';
import AttachPhotos from './AttachPhotos';

class Order {
  constructor($element, options = {}, params = {}) {
    if (!$element.length) {
      warn('Order requires an element');
      return;
    }

    this.options = {
      ...Order.DEFAULTS,
      ...params,
    };
    this.productOptions = {
      ...OrderProduct.DEFAULTS,
      ...params.product,
    };

    this.$el = $element;
    this.$list = this.$el.find('.order__list');
    this.reasons = options.reasons;
    this.commentMaxLength = options.commentMaxLength;

    this.template = $(this.options.PRODUCT).html();
    this.$add = $(this.options.ADD);
    this.unremovable = false;

    this.$products = $(this.productOptions.PRODUCT);
    this.nextIndex = this.$products.length;

    this.setCurrency(options.currency);
    this.handleAdd();
    this.handleCopy();

    if (this.$products.length) {
      this.$products.each((index, product) => {
        this.initProduct(index, $(product));
      });

      if (this.$products.length === 1) {
        this.unremovable = this.$products;
      }
    } else {
      this.unremovable = this.addProduct();
    }

    if (this.unremovable) {
      this.unremovable.data('product').setAsUnremovable();
    }
  }

  initProduct(index, $product) {
    $product.data({
      product: new OrderProduct($product, {
        currency: this.currency,
      }, this.productOptions),
      index,
    }).on(`remove${this.productOptions.NAMESPACE}`, () => {
      this.removeProduct();
    });

    if (this.reasons) {
      $product.data('reason', new Reason($product, {
        name: 'items_info',
        index,
        reasons: this.reasons,
        commentMaxLength: this.commentMaxLength,
        isOrder: true,
      }));
    }

    const $attachPhotos = $product.find('.attach-photos');
    if ($attachPhotos.length) {
      const attachPhotos = new AttachPhotos($attachPhotos, {
        onChange: () => this.syncPhotos($product, index, attachPhotos),
      });
      const photos = $attachPhotos.data('photos') || [];
      attachPhotos.reset(index, 0, photos[0] || []);
      $product.data('attachPhotos', attachPhotos);
    }
  }

  /**
   * Rebuild a product's hidden photo id fields from its uploader's current state
   * @param {jQuery} $product
   * @param {number} index
   * @param {AttachPhotos} attachPhotos
   */
  syncPhotos($product, index, attachPhotos) {
    $product.find('.js-item-photo').remove();

    const $attachPhotos = $product.find('.attach-photos');
    attachPhotos.getPhotoIds().forEach((photoId) => {
      $(`<input type="hidden" class="js-item-photo" name="item_photos[${index}][0][]" value="${photoId}" />`)
        .appendTo($attachPhotos);
    });
  }

  /**
   * @param {Object} [prefill] field values (by js-<field> class suffix) to copy into the new product
   */
  addProduct(prefill = {}) {
    const $product = $(parseTemplate(this.template, { index: this.nextIndex }));
    this.initProduct(this.nextIndex, $product);
    this.nextIndex += 1;

    Object.keys(prefill).forEach((key) => {
      const $field = $product.find(`.js-${key}`);
      if ($field.length && typeof prefill[key] !== 'undefined') {
        $field.val(prefill[key]).trigger(`change${this.productOptions.NAMESPACE}`);
      }
    });

    // TODO enable reason chooser module here
    if (this.unremovable) {
      this.unremovable.data('product').setAsRemovable();
      this.unremovable = false;
    }

    this.$list.append($product);
    this.$products = this.$products.add($product);
    return $product;
  }

  handleCopy() {
    this.$list.on(`click${this.options.NAMESPACE}`, this.productOptions.COPY, (event) => {
      const $source = $(event.currentTarget).closest(this.productOptions.PRODUCT);

      this.addProduct({
        'sku-code': $source.find('.js-sku-code').val(),
        'sku-desc': $source.find('.js-sku-desc').val(),
        'hs-code': $source.find('.js-hs-code').val(),
        quantity: $source.find('.js-quantity').val(),
        price: $source.find('.js-price').val(),
        'country-code': $source.find('.js-country-code').val(),
        'export-awb': $source.find('.js-export-awb').val(),
      });
    });
  }

  removeProduct() {
    const $products = this.$products.filter(`:not(.${this.productOptions.REMOVING})`);

    if ($products.length) {
      this.nextIndex = $products.last().data('index') + 1;

      if ($products.length === 1) {
        this.unremovable = $products;
        $products.data('product').setAsUnremovable();
      }
    } else {
      this.nextIndex = 0;
    }
  }

  handleAdd() {
    this.$add.on(`click${this.options.NAMESPACE}`, () => {
      this.addProduct();
    });
  }

  /**
   * Check that every product with a photo uploader has a valid number of photos attached
   * (enough, and not more than allowed), showing an error and expanding it if not
   * @returns {boolean}
   */
  hasValidPhotos() {
    if (!this.$products || this.$el.hasClass('hidden')) {
      return true;
    }

    let isValid = true;

    this.$products.each((i, element) => {
      const $product = $(element);
      const attachPhotos = $product.data('attachPhotos');

      if (!attachPhotos) {
        return;
      }

      if (attachPhotos.isUploading()) {
        attachPhotos.showUploadingMessage();
        $product.data('product').expand();
        isValid = false;
      } else if (!attachPhotos.isSatisfied()) {
        attachPhotos.showRequiredMessage();
        $product.data('product').expand();
        isValid = false;
      } else if (attachPhotos.isOverMaximum()) {
        attachPhotos.showMaximumError();
        $product.data('product').expand();
        isValid = false;
      }
    });

    return isValid;
  }

  setCurrency(currency = this.productOptions.CURRENCY) {
    this.currency = currency;
    this.$products.trigger(`currency${this.productOptions.NAMESPACE}`, [currency]);
  }
}

Order.DEFAULTS = {
  ELEMENT: '.order',
  PRODUCT: '#order__product-template',
  ADD: '.button_add',
  ACTION: '.order__group_action',
  REASON: '.order__group_reason',
  WISH: '.order__group_wish',
  COMMENT: '.order__group_comment',
  NAMESPACE: '.order',
};

export default Order;
